import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useHistory, useLocation } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import "../../auth.css";

const AUTH_STATUS_BASE = `https://${process.env.REACT_APP_ACCOUNT_API_URL}`;
const LS_KEY = "ph_prefill_email";

const ReqItem = ({ ok, children }) => (
  <li className={`ph-req ${ok ? "ok" : "bad"}`}>
    <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
      {ok ? "✓" : "✕"}
    </span>
    <span>{children}</span>
  </li>
);

function rememberEmail(key, v) {
  try {
    localStorage.setItem(key, v || "");
  } catch {}
}

function goForgotCustom(history, email) {
  rememberEmail(LS_KEY, email);
  const qs = email ? `?email=${encodeURIComponent(email)}` : "";
  // go to the MFA request page instead of old /auth/forgot
  history.push(`/auth/mfa/request${qs}`);
}

async function startMfaSetup({
  mfaUser,
  email,
  setBusy,
  setError,
  setTotpSecret,
  setOtpAuthUrl,
}) {
  if (!mfaUser) return;

  try {
    setBusy(true);
    setError("");

    // 1) Ask Cognito for the TOTP secret for this user
    const secret = await Auth.setupTOTP(mfaUser);
    setTotpSecret(secret || "");

    // 2) Build a standard otpauth:// URL (works with most authenticator apps)
    const issuer = process.env.REACT_APP_TOTP_ISSUER || "ACCOUNT";
    const labelEmail = (email || mfaUser?.username || "").trim() || "user";
    const label = `${issuer}:${labelEmail}`;

    const url =
      "otpauth://totp/" +
      encodeURIComponent(label) +
      `?secret=${secret}` +
      `&issuer=${encodeURIComponent(issuer)}` +
      `&algorithm=SHA1&digits=6&period=30`;

    setOtpAuthUrl(url);
  } catch (e) {
    console.error("Error starting MFA TOTP setup", e);
    setError(e?.message || "We couldn’t start MFA setup. Please try again.");
  } finally {
    setBusy(false);
  }
}

async function completeMfaSetup({
  mfaUser,
  mfaCode,
  setError,
  setStage,
  setBusy,
}) {
  if (!mfaUser) {
    setError("Session expired. Please sign in again to complete MFA setup.");
    setStage("EMAIL");
    return;
  }

  const code = (mfaCode || "").trim();
  if (!code) {
    setError("Please enter the 6-digit code from your authenticator app.");
    return;
  }

  setBusy(true);
  setError("");

  try {
    // 1) Verify the TOTP code against the secret Cognito generated
    await Auth.verifyTotpToken(mfaUser, code);

    // 2) Tell Cognito that this user's preferred MFA method is now TOTP
    await Auth.setPreferredMFA(mfaUser, "TOTP");

    // 3) Done → go into the app
    window.location.replace("/");
  } catch (e) {
    console.error("Error completing MFA setup", e);
    setError(e?.message || "We couldn’t verify your code. Please try again.");
  } finally {
    setBusy(false);
  }
}

async function completeMfaChallenge({
  mfaUser,
  mfaCode,
  setError,
  setStage,
  setBusy,
}) {
  if (!mfaUser) {
    setError("Session expired. Please sign in again to complete MFA verification.");
    setStage("EMAIL");
    return;
  }

  const code = (mfaCode || "").trim();
  if (!code) {
    setError("Please enter the 6-digit code from your authenticator app.");
    return;
  }

  setBusy(true);
  setError("");

  try {
    await Auth.confirmSignIn(mfaUser, code, "SOFTWARE_TOKEN_MFA");
    window.location.replace("/");
  } catch (e) {
    console.error("Error confirming MFA challenge", e);
    setError(e?.message || "We couldn’t verify your code. Please try again.");
  } finally {
    setBusy(false);
  }
}

async function completeNewPassword({
  userForNewPw,
  newPw1,
  allOk,
  matchOk,
  setError,
  setStage,
  setBusy,
}) {
  if (!userForNewPw) {
    setError("Session expired. Please sign in again with your temporary password.");
    setStage("PASSWORD");
    return;
  }
  if (!allOk) {
    setError("Your password does not meet the requirements.");
    return;
  }
  if (!matchOk) {
    setError("Passwords must match.");
    return;
  }

  setBusy(true);
  setError("");
  try {
    await Auth.completeNewPassword(userForNewPw, newPw1, {});
    window.location.replace("/");
  } catch (e) {
    setError(e?.message || "Could not set your new password. Please try again.");
  } finally {
    setBusy(false);
  }
}

function handleSubmit({
  e,
  busy,
  stage,
  continueOrReset,
  signInNative,
  completeNewPasswordAction,
  completeMfaSetupAction,
  completeMfaChallengeAction,
}) {
  e.preventDefault();
  if (busy) return;
  if (stage === "EMAIL") continueOrReset();
  else if (stage === "PASSWORD") signInNative();
  else if (stage === "NEW_PASSWORD") completeNewPasswordAction();
  else if (stage === "MFA_SETUP") completeMfaSetupAction();
  else if (stage === "MFA_CHALLENGE") completeMfaChallengeAction();
}

export default function LoginChooser() {
  const history = useHistory();
  const location = useLocation();

  // Stages: EMAIL -> PASSWORD -> (optional) NEW_PASSWORD -> MFA_SETUP / MFA_CHALLENGE
  const [stage, setStage] = useState("EMAIL");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // NEW_PASSWORD_REQUIRED inline
  const [newPw1, setNewPw1] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [userForNewPw, setUserForNewPw] = useState(null);

  // show/hide toggles for NEW_PASSWORD fields
  const [showNewPw1, setShowNewPw1] = useState(false);
  const [showNewPw2, setShowNewPw2] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef(null);

  // MFA: local TOTP setup + challenge
  const [mfaUser, setMfaUser] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [otpAuthUrl, setOtpAuthUrl] = useState("");

  // Prefill email from ?email= or localStorage
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const fromQS = (sp.get("email") || "").trim();
    const fromLS =
      (!fromQS && typeof localStorage !== "undefined" && localStorage.getItem(LS_KEY)) || "";
    setEmail(fromQS || fromLS || "");
  }, [location.search]);

  // Focus password when we move to that stage
  useEffect(() => {
    if (stage === "PASSWORD" && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [stage]);

  // When we enter MFA_SETUP, fetch the TOTP secret & build otpauth:// URL
  useEffect(() => {
    if (stage === "MFA_SETUP" && mfaUser && !totpSecret) {
      startMfaSetup({
        mfaUser,
        email,
        setBusy,
        setError,
        setTotpSecret,
        setOtpAuthUrl,
      });
    }
  }, [stage, mfaUser, totpSecret]); // startMfaSetup is defined below (function declaration, hoisted)

  async function sendCodeAndGo() {
    try {
      await Auth.forgotPassword(email.trim());
    } catch {
      // Avoid user enumeration; still proceed to code screen.
      }
    history.replace(`/auth/reset?email=${encodeURIComponent(email)}`, { sent: true });
  }

  // MFA: helper to start Cognito Hosted UI login (username/password + MFA)
  function startHostedLogin() {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID;
    const redirect =
      process.env.REACT_APP_REDIRECT_SIGN_IN || `${window.location.origin}/`;
    const scopes =
      (process.env.REACT_APP_OAUTH_SCOPES || "openid email profile")
        .split(" ")
        .join(" ");

    // MFA: if misconfigured locally, fall back to password stage instead of doing nothing
    if (!domain || !clientId) {
      console.error(
        "Hosted UI config missing REACT_APP_COGNITO_DOMAIN or REACT_APP_USER_POOL_WEB_CLIENT_ID"
      );
      setStage("PASSWORD"); // safe fallback
      return;
    }

    // Ensure full https:// URL for Hosted UI
    const base =
      domain && domain.startsWith("http")
        ? domain
        : `https://${domain || ""}`.replace(/\/+$/, "");

    const url =
      `${base}/login` +
      `?client_id=${encodeURIComponent(clientId || "")}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scopes)}` +
      `&redirect_uri=${encodeURIComponent(redirect)}` +
      (email ? `&login_hint=${encodeURIComponent(email.trim())}` : "");

    window.location.assign(url);
  }

  // Step 1: after email, decide reset vs login
  async function continueOrReset() {
    if (!email) return;
    setBusy(true);
    setError("");
    rememberEmail(LS_KEY, email);

    try {
      const r = await fetch(
        `${AUTH_STATUS_BASE}/auth/status?username=${encodeURIComponent(email)}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );
      const data = await r.json().catch(() => ({}));
      const reset = !!data.reset;

      if (reset) {
        goForgotCustom(history, email);
      } else {
        // MFA: for normal logins, use local password step (no Hosted UI)
        setStage("PASSWORD"); // show password field on the same screen
      }
    } catch {
      // MFA: if status API fails, still fall back to password step
      setStage("PASSWORD"); // legacy fallback (no Hosted UI)
      // startHostedLogin(); // (kept for reference, but no longer used)
    } finally {
      setBusy(false);
    }
  }

  // Step 2: native sign-in with Amplify
  async function signInNative() {
    if (!email || !password) return;
    setBusy(true);
    setError("");
    rememberEmail(LS_KEY, email);

    try {
      const user = await Auth.signIn(email.trim(), password);

      const challenge = user?.challengeName;

      if (challenge === "NEW_PASSWORD_REQUIRED") {
        setUserForNewPw(user);
        setStage("NEW_PASSWORD");
        return;
      }

      if (challenge === "MFA_SETUP") {
        // user must enroll TOTP (we'll handle in a later task)
        setMfaUser(user);
        setStage("MFA_SETUP");
        return;
      }

      if (challenge === "SOFTWARE_TOKEN_MFA") {
        // user already has TOTP; ask for 6-digit code
        setMfaUser(user);
        setStage("MFA_CHALLENGE");
        return;
      }

      // No challenges → signed in normally
      window.location.replace("/");
    } catch (e) {
      if (
        e?.challengeName === "NEW_PASSWORD_REQUIRED" ||
        /NEW_PASSWORD_REQUIRED/i.test(e?.message || "")
      ) {
        setUserForNewPw(e);
        setStage("NEW_PASSWORD");
        setBusy(false);
        return;
      }

      if (e?.challengeName === "SOFTWARE_TOKEN_MFA") {
        // Sometimes Amplify throws the challenge instead of returning it
        setMfaUser(e);
        setStage("MFA_CHALLENGE");
        setBusy(false);
        return;
      }

      const code = e?.code || e?.name || "";
      const msg = (e?.message || "").toLowerCase();

      if (code === "PasswordResetRequiredException" || msg.includes("password reset required")) {
        goForgotCustom(history, email);
      } else if (code === "InvalidPasswordException" && msg.includes("temporary")) {
        goForgotCustom(history, email);
      } else if (code === "UserNotConfirmedException") {
        setError(
          "Your email isn’t confirmed yet. Please check your inbox for a verification link."
        );
      } else if (code === "NotAuthorizedException" || code === "UserNotFoundException") {
        setError("Invalid email or password.");
      } else {
        setError(e?.message || "We couldn’t sign you in. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  // Step 3: complete NEW_PASSWORD_REQUIRED (no email code)
  const { req, allOk, matchOk } = useMemo(() => {
    const length = newPw1.length >= 12;
    const upper = /[A-Z]/.test(newPw1);
    const lower = /[a-z]/.test(newPw1);
    const number = /\d/.test(newPw1);
    const symbol = /[^A-Za-z0-9]/.test(newPw1);
    const reqObj = { length, upper, lower, number, symbol };
    const allOkVal = reqObj.length && reqObj.upper && reqObj.lower && reqObj.number && reqObj.symbol;
    const matchOkVal = newPw1.length > 0 && newPw1 === newPw2;
    return { req: reqObj, allOk: allOkVal, matchOk: matchOkVal };
  }, [newPw1, newPw2]);

  const handleSubmitLocal = useCallback((e) => {
    handleSubmit({
      e,
      busy,
      stage,
      continueOrReset,
      signInNative,
      completeNewPasswordAction: () =>
        completeNewPassword({
          userForNewPw,
          newPw1,
          allOk,
          matchOk,
          setError,
          setStage,
          setBusy,
        }),
      completeMfaSetupAction: () =>
        completeMfaSetup({
          mfaUser,
          mfaCode,
          setError,
          setStage,
          setBusy,
        }),
      completeMfaChallengeAction: () =>
        completeMfaChallenge({
          mfaUser,
          mfaCode,
          setError,
          setStage,
          setBusy,
        }),
    });
  }, [busy, stage, continueOrReset, signInNative, userForNewPw, newPw1, allOk, matchOk, mfaUser, mfaCode]);

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">
          {stage === "NEW_PASSWORD" ? "Set a new password" : "Sign in to your account"}
        </h1>

        <form onSubmit={handleSubmitLocal}>
          {/* Email step */}
          <label className="ph-label" htmlFor="ph-email">
            Email Address
          </label>
          <input
            id="ph-email"
            className="ph-field"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            disabled={busy || stage !== "EMAIL"}
          />

          {/* Password step */}
          {stage === "PASSWORD" && (
            <>
              <label className="ph-label" htmlFor="ph-password">
                Password
              </label>
              <input
                id="ph-password"
                className="ph-field"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                ref={passwordRef}
                disabled={busy}
              />
            </>
          )}

          {/* NEW_PASSWORD_REQUIRED with show/hide toggles */}
          {stage === "NEW_PASSWORD" && (
            <>
              <label className="ph-label" htmlFor="ph-new1">
                New Password
              </label>
              <div className="ph-field-wrap">
                <input
                  id="ph-new1"
                  className={`ph-field ${newPw1 && !allOk ? "invalid" : ""}`}
                  type={showNewPw1 ? "text" : "password"}
                  value={newPw1}
                  onChange={(e) => setNewPw1(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Enter a new password"
                />
                <button
                  type="button"
                  className="ph-toggle"
                  onClick={() => setShowNewPw1((s) => !s)}
                  aria-label={showNewPw1 ? "Hide password" : "Show password"}
                  aria-pressed={showNewPw1}
                  disabled={busy}
                >
                  {showNewPw1 ? "Hide" : "Show"}
                </button>
              </div>

              <ul
                className="ph-req-list"
                aria-live="polite"
                aria-atomic="true"
                style={{ margin: "8px 0 12px" }}
              >
                <ReqItem ok={req.length}>At least 12 characters</ReqItem>
                <ReqItem ok={req.upper}>Contains an uppercase letter (A–Z)</ReqItem>
                <ReqItem ok={req.lower}>Contains a lowercase letter (a–z)</ReqItem>
                <ReqItem ok={req.number}>Contains a number (0–9)</ReqItem>
                <ReqItem ok={req.symbol}>Contains a symbol (e.g., !@#$%)</ReqItem>
              </ul>

              <label className="ph-label" htmlFor="ph-new2">
                Confirm New Password
              </label>
              <div className="ph-field-wrap">
                <input
                  id="ph-new2"
                  className="ph-field"
                  type={showNewPw2 ? "text" : "password"}
                  value={newPw2}
                  onChange={(e) => setNewPw2(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Re-enter new password"
                />
                <button
                  type="button"
                  className="ph-toggle"
                  onClick={() => setShowNewPw2((s) => !s)}
                  aria-label={showNewPw2 ? "Hide password" : "Show password"}
                  aria-pressed={showNewPw2}
                  disabled={busy}
                >
                  {showNewPw2 ? "Hide" : "Show"}
                </button>
              </div>

              <div
                className={`ph-req ${matchOk ? "ok" : "bad"}`}
                aria-live="polite"
                style={{ margin: "6px 0 12px" }}
              >
                <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
                  {matchOk ? "✓" : "✕"}
                </span>
                Passwords match
              </div>
            </>
          )}

          {/* MFA_SETUP: show QR/secret + 6-digit code input */}
          {stage === "MFA_SETUP" && (
            <>
              <p className="ph-hint" style={{ marginTop: 16 }}>
                Scan this code with your authenticator app (Microsoft Authenticator, Google
                Authenticator, etc.).
              </p>

              {/* Placeholder for QR; we’ll wire the real QR in a later task */}
              <div
                className="ph-qr-placeholder"
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                  marginBottom: 12,
                }}
              >
                {otpAuthUrl ? (
                  <QRCodeCanvas value={otpAuthUrl} size={180} />
                ) : (
                  "Preparing QR code…"
                )}
              </div>

              <p className="ph-hint" style={{ fontSize: 12, wordBreak: "break-all" }}>
                Or enter this code manually in your app:{" "}
                <code>{totpSecret || "••••••••••••"}</code>
              </p>

              {otpAuthUrl && (
                <p className="ph-hint" style={{ fontSize: 11, wordBreak: "break-all" }}>
                  Advanced (URI): <code>{otpAuthUrl}</code>
                </p>
              )}

              <label
                className="ph-label"
                htmlFor="ph-mfa-setup-code"
                style={{ marginTop: 12 }}
              >
                6-digit code from your authenticator app
              </label>
              <input
                id="ph-mfa-setup-code"
                className="ph-field"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={busy}
                autoComplete="one-time-code"
              />
            </>
          )}

          {/* MFA_CHALLENGE: only 6-digit code (TOTP already set up) */}
          {stage === "MFA_CHALLENGE" && (
            <>
              <label
                className="ph-label"
                htmlFor="ph-mfa-challenge-code"
                style={{ marginTop: 16 }}
              >
                6-digit code from your authenticator app
              </label>
              <input
                id="ph-mfa-challenge-code"
                className="ph-field"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={busy}
                autoComplete="one-time-code"
              />
            </>
          )}

          {error && (
            <div className="ph-error" role="alert">
              {error}
            </div>
          )}

          <button
            className="ph-button"
            type="submit"
            disabled={
              busy ||
              !email ||
              (stage === "PASSWORD" && !password) ||
              (stage === "NEW_PASSWORD" && (!newPw1 || !newPw2)) ||
              (stage === "MFA_SETUP" && !mfaCode) ||
              (stage === "MFA_CHALLENGE" && !mfaCode)
            }
          >
            {busy
              ? stage === "PASSWORD"
                ? "Signing in…"
                : stage === "NEW_PASSWORD"
                ? "Updating…"
                : stage === "MFA_SETUP" || stage === "MFA_CHALLENGE"
                ? "Verifying…"
                : "Checking…"
              : stage === "PASSWORD"
              ? "Sign in"
              : stage === "NEW_PASSWORD"
              ? "Set password"
              : stage === "MFA_SETUP"
              ? "Verify & continue"
              : stage === "MFA_CHALLENGE"
              ? "Verify & sign in"
              : "Continue"}
          </button>
        </form>

        {stage !== "NEW_PASSWORD" && (
          <p className="ph-hint">
            <a
              className="ph-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goForgotCustom(history, email);
              }}
            >
              Reset your password
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
